import type { OrganizationReferenceDataAccessResponse } from '@/types/api/organizations'
import type { SecurityWhere } from './security-types'

import {
  AssetType,
  BondType,
  Currency,
} from '@moment/trading-types/reference-data'

import { qualifySecurityField } from './translate-filter-object'

export type SecurityReferenceDataAccess = Pick<
  OrganizationReferenceDataAccessResponse,
  'international_bonds' | 'securitized'
> &
  Partial<Pick<OrganizationReferenceDataAccessResponse, 'fixed_income'>>

// Live inventory returns no rows to an org without fixed-income access.
const MATCHES_NO_ROWS: SecurityWhere = {
  and: [
    {
      [qualifySecurityField('asset_type')]: { equal_to: AssetType.FixedIncome },
    },
    {
      [qualifySecurityField('asset_type')]: {
        not_equal_to: AssetType.FixedIncome,
      },
    },
  ],
}

/**
 * The org's reference-data entitlements as Security predicates. Security has
 * no server-side row policy, so every Security read must AND this in. Like
 * live inventory, fixed income is allowed unless the org's access says not.
 */
export function securityAccessWhere(
  access: SecurityReferenceDataAccess,
): SecurityWhere[] {
  if (access.fixed_income === false) return [MATCHES_NO_ROWS]
  const clauses: SecurityWhere[] = [
    {
      // Security files money market funds under asset_type fund; live inventory serves them as bonds.
      or: [
        {
          [qualifySecurityField('asset_type')]: {
            equal_to: AssetType.FixedIncome,
          },
        },
        {
          [qualifySecurityField('asset_class')]: {
            equal_to: BondType.MoneyMarket,
          },
        },
      ],
    },
  ]
  if (!access.international_bonds) {
    clauses.push(
      { [qualifySecurityField('currency')]: { equal_to: Currency.USD } },
      { [qualifySecurityField('security_id')]: { starts_with: 'US' } },
    )
  }
  if (!access.securitized) {
    clauses.push({
      or: [
        {
          [qualifySecurityField('asset_class')]: {
            not_equal_to: BondType.Securitized,
          },
        },
        { [qualifySecurityField('asset_class')]: { is_null: true } },
      ],
    })
  }
  return clauses
}
